import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  MenuItem,
  Snackbar,
  TextField,
  Typography
} from '@material-ui/core'
import { Add, DeleteOutline, Edit, Refresh, Save } from '@material-ui/icons'
import { makeStyles } from '@material-ui/core/styles'
import L from 'leaflet'
import 'leaflet/dist/leaflet.css'
import '@geoman-io/leaflet-geoman-free'
import '@geoman-io/leaflet-geoman-free/dist/leaflet-geoman.css'

const DEFAULT_CENTER = [33.55, -112.0]

const useStyles = makeStyles((theme) => ({
  root: {
    borderRadius: 8,
    boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
    border: '1px solid rgba(205, 216, 209, 0.9)',
    overflow: 'hidden'
  },
  sidebar: {
    height: '100%',
    background: 'linear-gradient(180deg, #fbfcfb 0%, #f4f8f5 100%)',
    borderRight: '1px solid rgba(205, 216, 209, 0.9)'
  },
  sectionTitle: {
    fontWeight: 700,
    color: '#20342b'
  },
  helperText: {
    color: '#5d6c64'
  },
  actionRow: {
    display: 'flex',
    gap: theme.spacing(1),
    flexWrap: 'wrap',
    marginTop: theme.spacing(2)
  },
  mapContainer: {
    width: '100%',
    minHeight: 640,
    height: '100%',
    backgroundColor: '#f4f8f5'
  },
  territoryList: {
    marginTop: theme.spacing(2),
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1)
  },
  territoryItem: {
    borderRadius: 8,
    border: '1px solid #dce6df',
    padding: theme.spacing(1.5),
    cursor: 'pointer',
    background: '#fff',
    transition: 'border-color 120ms ease, box-shadow 120ms ease, transform 120ms ease'
  },
  territoryItemSelected: {
    borderColor: '#2f6b53',
    boxShadow: '0 2px 6px rgba(0,0,0,0.12)',
    transform: 'translateY(-1px)'
  },
  territoryName: {
    fontWeight: 700,
    color: '#20342b'
  },
  territoryMeta: {
    color: '#5d6c64',
    marginTop: theme.spacing(0.5)
  },
  chipRow: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(1),
    marginTop: theme.spacing(1.25)
  },
  emptyState: {
    borderRadius: 8,
    border: '1px dashed #c8d7ce',
    padding: theme.spacing(2),
    background: 'rgba(255,255,255,0.72)'
  },
  mapWrap: {
    height: '100%'
  },
  statusChip: {
    fontWeight: 700
  },
  '@global': {
    '.subcontractor-territory-label': {
      background: 'rgba(255,255,255,0.82)',
      border: '1px solid rgba(26, 43, 35, 0.15)',
      borderRadius: '6px',
      boxShadow: 'none',
      color: '#20342b',
      fontWeight: 700,
      padding: '2px 8px',
      pointerEvents: 'none'
    }
  }
}))

function getLayerStableId (layer) {
  return String(layer?.serverId || layer?._territoryKey || layer?._leaflet_id || '')
}

function addLabelToPolygon (layer, territoryName) {
  if (layer.getTooltip()) {
    layer.unbindTooltip()
  }

  layer.bindTooltip(territoryName || 'Unnamed Territory', {
    permanent: true,
    direction: 'center',
    className: 'subcontractor-territory-label'
  })
}

function generateDistinctColorMap (territories = []) {
  const colorMap = {}
  const visuallyDistinctOrder = [0, 180, 90, 270, 45, 225, 135, 315]

  territories.forEach((territory, index) => {
    const key = String(territory?._id || territory?._territoryKey || `territory-${index}`)
    const hue = visuallyDistinctOrder[index % visuallyDistinctOrder.length]
    colorMap[key] = `hsl(${hue}, 68%, 46%)`
  })

  return colorMap
}

function extractCoordinatesFromLayer (layer) {
  const latLngs = layer.getLatLngs()
  const rings = Array.isArray(latLngs?.[0]) ? latLngs : [latLngs]

  return rings.map((ring) => ring.map((latLng) => ({
    lat: latLng.lat,
    lng: latLng.lng
  })))
}

function buildTerritoryItemFromLayer (layer, crewsById) {
  const assignedCrew = layer.assignedCrew || ''
  const crew = crewsById[String(assignedCrew)] || null
  const coordinates = extractCoordinatesFromLayer(layer)
  const points = coordinates.reduce((sum, ring) => sum + ring.length, 0)

  return {
    _id: layer.serverId || null,
    layerId: getLayerStableId(layer),
    name: layer.territoryName || 'Unnamed Territory',
    assignedCrew,
    assignedCrewName: crew?.name || 'Unassigned',
    coverageAreaLabel: layer.coverageAreaLabel || 'Save to calculate',
    propertyCount: Number(layer.propertyCount || 0),
    jobsiteCount: Number(layer.jobsiteCount || 0),
    coordinates,
    points
  }
}

function createPolygonLayer (polygon) {
  const latLngRings = (polygon.coordinates || []).map((ring) => (
    ring.map((coord) => L.latLng(coord.lat, coord.lng))
  ))

  const layer = L.polygon(latLngRings)
  layer.serverId = polygon._id || null
  layer._territoryKey = polygon._id || `territory-${Date.now()}-${Math.random()}`
  layer.territoryName = polygon.name || 'Unnamed Territory'
  layer.assignedCrew = polygon.assignedCrew || ''
  layer.coverageAreaLabel = polygon.coverageAreaLabel || 'Save to calculate'
  layer.propertyCount = Number(polygon.propertyCount || 0)
  layer.jobsiteCount = Number(polygon.jobsiteCount || 0)
  return layer
}

function getTerritorySiteCountLabel (territory) {
  const count = Number(territory?.jobsiteCount || territory?.propertyCount || 0)
  return `${count} ${count === 1 ? 'property/jobsite' : 'properties/jobsites'}`
}

const SubcontractorTerritoryMapEditor = ({
  items,
  crews,
  loading,
  saving,
  onSave,
  onRefresh
}) => {
  const classes = useStyles()
  const mapRef = useRef(null)
  const mapContainerRef = useRef(null)
  const drawnItemsRef = useRef(null)
  const drawingModeRef = useRef(false)
  const editingModeRef = useRef(false)
  const dialogOpenRef = useRef(false)
  const dialogTargetLayerRef = useRef(null)
  const resizeObserverRef = useRef(null)

  const [territories, setTerritories] = useState([])
  const [selectedLayerId, setSelectedLayerId] = useState('')
  const [dialogTargetLayerId, setDialogTargetLayerId] = useState('')
  const [selectedPolygon, setSelectedPolygon] = useState(null)
  const [dialogOpen, setDialogOpen] = useState(false)
  const [territoryName, setTerritoryName] = useState('')
  const [selectedCrew, setSelectedCrew] = useState('')
  const [drawingMode, setDrawingMode] = useState(false)
  const [editingMode, setEditingMode] = useState(false)
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false)
  const [notification, setNotification] = useState({ open: false, message: '' })
  const editNotificationShownRef = useRef(false)

  const crewsById = useMemo(() => {
    return crews.reduce((acc, crew) => {
      acc[String(crew._id)] = crew
      return acc
    }, {})
  }, [crews])

  const colorMap = useMemo(() => {
    return generateDistinctColorMap(items.map((item, index) => ({
      ...item,
      _territoryKey: item?._id || `territory-${index}`
    })))
  }, [items])

  useEffect(() => {
    drawingModeRef.current = drawingMode
  }, [drawingMode])

  useEffect(() => {
    editingModeRef.current = editingMode
    if (!editingMode) {
      editNotificationShownRef.current = false
    }
  }, [editingMode])

  useEffect(() => {
    dialogOpenRef.current = dialogOpen
  }, [dialogOpen])

  const updateLocalTerritories = useCallback(() => {
    if (!drawnItemsRef.current) return

    const nextTerritories = []
    drawnItemsRef.current.eachLayer((layer) => {
      if (layer instanceof L.Polygon) {
        nextTerritories.push(buildTerritoryItemFromLayer(layer, crewsById))
      }
    })

    nextTerritories.sort((a, b) => a.name.localeCompare(b.name))
    setTerritories(nextTerritories)
  }, [crewsById])

  const applyLayerStyle = useCallback((layer) => {
    const layerId = getLayerStableId(layer)
    const baseColor = colorMap[layerId] || '#3388ff'
    const isSelected = layerId === selectedLayerId

    layer.setStyle({
      color: baseColor,
      fillColor: baseColor,
      fillOpacity: isSelected ? 0.35 : 0.22,
      weight: isSelected ? 4 : 2
    })
  }, [colorMap, selectedLayerId])

  const refreshLayerStyles = useCallback(() => {
    if (!drawnItemsRef.current) return

    drawnItemsRef.current.eachLayer((layer) => {
      if (layer instanceof L.Polygon) {
        applyLayerStyle(layer)
      }
    })
  }, [applyLayerStyle])

  const openDialogForLayer = useCallback((layer) => {
    const layerId = getLayerStableId(layer)
    dialogTargetLayerRef.current = layer
    setSelectedPolygon(layer)
    setSelectedLayerId(layerId)
    setDialogTargetLayerId(layerId)
    setTerritoryName(layer.territoryName || '')
    setSelectedCrew(layer.assignedCrew || '')
    setDialogOpen(true)
  }, [])

  const bindLayerEvents = useCallback((layer) => {
    layer.on('click', (event) => {
      L.DomEvent.stopPropagation(event)
      setSelectedLayerId(getLayerStableId(layer))
      setSelectedPolygon(layer)
      if (!drawingModeRef.current && !editingModeRef.current) {
        openDialogForLayer(layer)
      } else {
        setTerritoryName(layer.territoryName || '')
        setSelectedCrew(layer.assignedCrew || '')
      }
    })
  }, [openDialogForLayer])

  const findLayerById = useCallback((layerId) => {
    if (!drawnItemsRef.current || !layerId) return null

    let matchedLayer = null
    drawnItemsRef.current.eachLayer((layer) => {
      if (matchedLayer || !(layer instanceof L.Polygon)) return
      if (getLayerStableId(layer) === layerId) {
        matchedLayer = layer
      }
    })

    return matchedLayer
  }, [])

  const syncMapFromItems = useCallback(() => {
    if (!mapRef.current || !drawnItemsRef.current) {
      return
    }

    drawnItemsRef.current.clearLayers()

    items.forEach((polygon, index) => {
      const layer = createPolygonLayer({
        ...polygon,
        _territoryKey: polygon._id || `territory-${index}`
      })

      addLabelToPolygon(layer, layer.territoryName)
      bindLayerEvents(layer)
      drawnItemsRef.current.addLayer(layer)

      if (layer.pm) {
        layer.pm.enable({
          allowSelfIntersection: false,
          snappable: true,
          snapDistance: 20
        })
        layer.pm.disable()
      }
    })

    setHasUnsavedChanges(false)
    dialogTargetLayerRef.current = null
    setSelectedPolygon(null)
    setSelectedLayerId('')
    setDialogTargetLayerId('')
    updateLocalTerritories()

    window.setTimeout(() => {
      if (drawnItemsRef.current?.getBounds()?.isValid()) {
        mapRef.current.fitBounds(drawnItemsRef.current.getBounds(), {
          padding: [32, 32]
        })
      } else {
        mapRef.current.setView(DEFAULT_CENTER, 12)
      }
      mapRef.current.invalidateSize()
    }, 60)
  }, [bindLayerEvents, items, updateLocalTerritories])

  useEffect(() => {
    if (!mapContainerRef.current || mapRef.current) {
      return
    }

    const map = L.map(mapContainerRef.current, {
      center: DEFAULT_CENTER,
      zoom: 12,
      zoomControl: true
    })

    L.tileLayer('https://mt1.google.com/vt/lyrs=y&x={x}&y={y}&z={z}', {
      attribution: '&copy; Google Maps',
      maxZoom: 20,
      minZoom: 0
    }).addTo(map)

    const drawnItems = new L.FeatureGroup()
    map.addLayer(drawnItems)
    mapRef.current = map
    drawnItemsRef.current = drawnItems

    map.pm.setGlobalOptions({
      snappable: true,
      snapDistance: 20,
      allowSelfIntersection: false,
      templineStyle: { color: '#2f6b53' },
      hintlineStyle: { color: '#2f6b53', dashArray: [5, 5] },
      pathOptions: {
        color: '#2f6b53',
        fillColor: '#2f6b53',
        fillOpacity: 0.18
      }
    })

    map.on('click', () => {
      if (dialogOpenRef.current) {
        return
      }
      setSelectedLayerId('')
      setSelectedPolygon(null)
    })

    map.on('pm:create', (event) => {
      const layer = event.layer
      layer.serverId = null
      layer._territoryKey = `new-${Date.now()}-${Math.random()}`
      layer.territoryName = 'New Territory'
      layer.assignedCrew = ''

      drawnItems.addLayer(layer)
      bindLayerEvents(layer)
      addLabelToPolygon(layer, layer.territoryName)

      if (layer.pm) {
        layer.pm.enable({
          allowSelfIntersection: false,
          snappable: true,
          snapDistance: 20
        })
        layer.pm.disable()
      }

      setHasUnsavedChanges(true)
      setDrawingMode(false)
      map.pm.disableDraw()
      updateLocalTerritories()
      openDialogForLayer(layer)
    })

    map.on('pm:update', () => {
      setHasUnsavedChanges(true)
      updateLocalTerritories()
      setNotification({
        open: true,
        message: 'Territory boundary updated. Save when you are ready.'
      })
    })

    map.on('pm:edit', () => {
      setHasUnsavedChanges(true)
      updateLocalTerritories()
      if (editingModeRef.current && !editNotificationShownRef.current) {
        editNotificationShownRef.current = true
        setNotification({
          open: true,
          message: 'Shape edit captured. Click Finish Editing, then Save.'
        })
      }
    })

    map.on('pm:remove', () => {
      setHasUnsavedChanges(true)
      updateLocalTerritories()
      setNotification({
        open: true,
        message: 'Territory removed locally. Save to persist the change.'
      })
    })

    const handleMapResize = () => {
      window.requestAnimationFrame(() => {
        if (!mapRef.current) return
        mapRef.current.invalidateSize()
      })
    }

    handleMapResize()

    if (typeof ResizeObserver !== 'undefined') {
      resizeObserverRef.current = new ResizeObserver(() => {
        handleMapResize()
      })
      resizeObserverRef.current.observe(mapContainerRef.current)
    }

    window.addEventListener('resize', handleMapResize)

    return () => {
      window.removeEventListener('resize', handleMapResize)
      if (resizeObserverRef.current) {
        resizeObserverRef.current.disconnect()
        resizeObserverRef.current = null
      }
      map.remove()
      mapRef.current = null
      drawnItemsRef.current = null
    }
  }, [bindLayerEvents, findLayerById, openDialogForLayer, updateLocalTerritories])

  useEffect(() => {
    syncMapFromItems()
  }, [syncMapFromItems])

  useEffect(() => {
    refreshLayerStyles()
  }, [refreshLayerStyles])

  const disableDrawMode = useCallback(() => {
    if (!mapRef.current) return
    mapRef.current.pm.disableDraw()
    setDrawingMode(false)
  }, [])

  const disableEditMode = useCallback(() => {
    if (!drawnItemsRef.current) return

    drawnItemsRef.current.eachLayer((layer) => {
      if (layer instanceof L.Polygon && layer.pm) {
        layer.pm.disable()
      }
    })

    setEditingMode(false)
  }, [])

  const finishEditingMode = useCallback(() => {
    disableEditMode()
    updateLocalTerritories()
    setHasUnsavedChanges(true)
    setNotification({
      open: true,
      message: 'Polygon edits saved locally. Click Save to persist them.'
    })
  }, [disableEditMode, updateLocalTerritories])

  const handleDrawPolygon = () => {
    if (!mapRef.current) return

    disableEditMode()
    mapRef.current.pm.enableDraw('Polygon', {
      snappable: true,
      snapDistance: 20,
      allowSelfIntersection: false,
      finishOn: 'dblclick',
      pathOptions: {
        color: '#2f6b53',
        fillColor: '#2f6b53',
        fillOpacity: 0.18
      }
    })
    setDrawingMode(true)
    setNotification({
      open: true,
      message: 'Draw mode enabled. Click to create vertices and double-click to finish.'
    })
  }

  const toggleEditMode = () => {
    if (!drawnItemsRef.current) return

    if (editingMode) {
      finishEditingMode()
      return
    }

    disableDrawMode()
    drawnItemsRef.current.eachLayer((layer) => {
      if (layer instanceof L.Polygon && layer.pm) {
        layer.pm.enable({
          snappable: true,
          snapDistance: 20,
          allowSelfIntersection: false
        })
      }
    })
    setEditingMode(true)
    setNotification({
      open: true,
      message: 'Edit mode enabled. Drag vertices to reshape saved polygons.'
    })
  }

  const handleSelectTerritory = (territory) => {
    setSelectedLayerId(territory.layerId)

    if (!drawnItemsRef.current || !mapRef.current) return

    drawnItemsRef.current.eachLayer((layer) => {
      if (!(layer instanceof L.Polygon)) return

      const layerId = getLayerStableId(layer)
      if (layerId === territory.layerId) {
        dialogTargetLayerRef.current = layer
        setSelectedPolygon(layer)
        setTerritoryName(layer.territoryName || '')
        setSelectedCrew(layer.assignedCrew || '')

        const bounds = layer.getBounds()
        if (bounds?.isValid()) {
          mapRef.current.fitBounds(bounds, { padding: [48, 48] })
        }
      }
    })
  }

  const closeDialog = () => {
    setDialogOpen(false)
    setDialogTargetLayerId('')
    dialogTargetLayerRef.current = null
  }

  const assignTerritory = () => {
    const targetLayer = dialogTargetLayerRef.current || selectedPolygon || findLayerById(dialogTargetLayerId || selectedLayerId)
    if (!targetLayer) {
      setNotification({
        open: true,
        message: 'Could not find that territory shape. Try selecting it again.'
      })
      return
    }

    targetLayer.territoryName = territoryName.trim() || 'Unnamed Territory'
    targetLayer.assignedCrew = selectedCrew || ''
    addLabelToPolygon(targetLayer, targetLayer.territoryName)

    closeDialog()
    setSelectedPolygon(targetLayer)
    setSelectedLayerId(getLayerStableId(targetLayer))
    setHasUnsavedChanges(true)
    updateLocalTerritories()
    refreshLayerStyles()
    setNotification({
      open: true,
      message: 'Territory details saved locally. Click Save to persist.'
    })
  }

  const handleDeleteSelected = () => {
    const targetLayer = dialogTargetLayerRef.current || selectedPolygon || findLayerById(dialogTargetLayerId || selectedLayerId)
    if (!targetLayer || !drawnItemsRef.current) return

    drawnItemsRef.current.removeLayer(targetLayer)
    closeDialog()
    setSelectedPolygon(null)
    setSelectedLayerId('')
    setHasUnsavedChanges(true)
    updateLocalTerritories()
  }

  const handleSave = async () => {
    if (!drawnItemsRef.current) return

    const nextTerritories = []
    drawnItemsRef.current.eachLayer((layer) => {
      if (layer instanceof L.Polygon) {
        nextTerritories.push({
          name: layer.territoryName || 'Unnamed Territory',
          assignedCrew: layer.assignedCrew || null,
          coordinates: extractCoordinatesFromLayer(layer),
          active: true
        })
      }
    })

    try {
      await onSave(nextTerritories)
      setHasUnsavedChanges(false)
      setNotification({
        open: true,
        message: 'Territories saved successfully.'
      })
    } catch (error) {
      setNotification({
        open: true,
        message: error?.message || 'Failed to save territories.'
      })
    }
  }

  const selectedTerritory = territories.find((territory) => territory.layerId === selectedLayerId) || null

  return (
    <>
      <Card className={classes.root}>
        <Grid container>
          <Grid item xs={12} md={4} lg={3}>
            <div className={classes.sidebar}>
              <CardContent>
                <Typography variant="h6" className={classes.sectionTitle}>
                  Territory Editor
                </Typography>
                <Typography variant="body2" className={classes.helperText}>
                  Draw subcontractor-owned territories, assign them to crews, and save the full vendor territory set back to the server.
                </Typography>
                {editingMode ? (
                  <Box mt={1.5}>
                    <Typography variant="body2" className={classes.helperText}>
                      After reshaping a polygon, click <strong>Finish Editing</strong>, then <strong>Save</strong>.
                    </Typography>
                  </Box>
                ) : null}

                <div className={classes.actionRow}>
                  <Button
                    color="primary"
                    variant="contained"
                    startIcon={<Add />}
                    onClick={handleDrawPolygon}
                    disabled={loading || saving}
                  >
                    Draw
                  </Button>
                  <Button
                    variant="outlined"
                    startIcon={<Edit />}
                    onClick={toggleEditMode}
                    disabled={loading || saving || !territories.length}
                  >
                    {editingMode ? 'Finish Editing' : 'Edit Shapes'}
                  </Button>
                  <Button
                    variant="outlined"
                    startIcon={<Refresh />}
                    onClick={onRefresh}
                    disabled={loading || saving}
                  >
                    Reload
                  </Button>
                  <Button
                    color="primary"
                    variant={hasUnsavedChanges ? 'contained' : 'outlined'}
                    startIcon={<Save />}
                    onClick={handleSave}
                    disabled={loading || saving || !hasUnsavedChanges}
                  >
                    Save
                  </Button>
                </div>

                <Box className={classes.chipRow}>
                  <Chip
                    size="small"
                    label={drawingMode ? 'Draw Mode On' : 'Draw Mode Off'}
                    className={classes.statusChip}
                    style={drawingMode ? { backgroundColor: '#e5f4ea', color: '#1b5e20' } : { backgroundColor: '#eceff1', color: '#455a64' }}
                  />
                  <Chip
                    size="small"
                    label={editingMode ? 'Edit Mode On' : 'Edit Mode Off'}
                    className={classes.statusChip}
                    style={editingMode ? { backgroundColor: '#fff3d9', color: '#8c5b00' } : { backgroundColor: '#eceff1', color: '#455a64' }}
                  />
                  <Chip
                    size="small"
                    label={hasUnsavedChanges ? 'Unsaved Changes' : 'Saved'}
                    className={classes.statusChip}
                    style={hasUnsavedChanges ? { backgroundColor: '#eef2ff', color: '#3547a5' } : { backgroundColor: '#e5f4ea', color: '#1b5e20' }}
                  />
                </Box>

                <Divider style={{ marginTop: 16, marginBottom: 16 }} />

                <Typography variant="subtitle2" className={classes.sectionTitle}>
                  Territories
                </Typography>

                <div className={classes.territoryList}>
                  {territories.length ? territories.map((territory) => (
                    <div
                      key={territory.layerId}
                      className={`${classes.territoryItem} ${territory.layerId === selectedLayerId ? classes.territoryItemSelected : ''}`}
                      onClick={() => handleSelectTerritory(territory)}
                      role="button"
                      tabIndex={0}
                      onKeyDown={(event) => {
                        if (event.key === 'Enter' || event.key === ' ') {
                          event.preventDefault()
                          handleSelectTerritory(territory)
                        }
                      }}
                    >
                      <Typography variant="body2" className={classes.territoryName}>
                        {territory.name}
                      </Typography>
                      <Typography variant="body2" className={classes.territoryMeta}>
                        {territory.assignedCrewName}
                      </Typography>
                      <Box className={classes.chipRow}>
                        <Chip size="small" label={territory.coverageAreaLabel || 'Save to calculate'} />
                        <Chip size="small" label={getTerritorySiteCountLabel(territory)} />
                        <Chip size="small" label={`${territory.coordinates.length} ring${territory.coordinates.length === 1 ? '' : 's'}`} />
                        <Chip size="small" label={`${territory.points} point${territory.points === 1 ? '' : 's'}`} />
                      </Box>
                    </div>
                  )) : (
                    <div className={classes.emptyState}>
                      <Typography variant="body2" className={classes.helperText}>
                        No territories yet. Draw a polygon on the map to create the first subcontractor boundary.
                      </Typography>
                    </div>
                  )}
                </div>

                {selectedTerritory ? (
                  <>
                    <Divider style={{ marginTop: 16, marginBottom: 16 }} />
                    <Typography variant="subtitle2" className={classes.sectionTitle}>
                      Selected Territory
                    </Typography>
                    <Box mt={1}>
                      <Typography variant="body2" className={classes.territoryName}>
                        {selectedTerritory.name}
                      </Typography>
                      <Typography variant="body2" className={classes.helperText}>
                        {selectedTerritory.assignedCrewName}
                      </Typography>
                      <Box className={classes.chipRow}>
                        <Chip size="small" label={selectedTerritory.coverageAreaLabel || 'Save to calculate'} />
                        <Chip size="small" label={getTerritorySiteCountLabel(selectedTerritory)} />
                      </Box>
                    </Box>
                    <div className={classes.actionRow}>
                      <Button
                        variant="outlined"
                        onClick={() => {
                          const targetLayer = selectedPolygon || findLayerById(selectedLayerId)
                          if (targetLayer) {
                            openDialogForLayer(targetLayer)
                          }
                        }}
                      >
                        Edit Details
                      </Button>
                    </div>
                  </>
                ) : null}
              </CardContent>
            </div>
          </Grid>

          <Grid item xs={12} md={8} lg={9}>
            <div className={classes.mapWrap}>
              <div ref={mapContainerRef} className={classes.mapContainer} />
            </div>
          </Grid>
        </Grid>
      </Card>

      <Dialog open={dialogOpen} onClose={closeDialog} maxWidth="sm" fullWidth>
        <DialogTitle>Territory Details</DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Territory Name"
                variant="outlined"
                value={territoryName}
                onChange={(event) => setTerritoryName(event.target.value)}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                select
                fullWidth
                label="Assigned Crew"
                variant="outlined"
                value={selectedCrew}
                onChange={(event) => setSelectedCrew(event.target.value)}
              >
                <MenuItem value="">Unassigned</MenuItem>
                {crews.map((crew) => (
                  <MenuItem key={crew._id} value={crew._id}>
                    {crew.name}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button
            color="secondary"
            startIcon={<DeleteOutline />}
            onClick={handleDeleteSelected}
          >
            Delete
          </Button>
          <Box flexGrow={1} />
          <Button onClick={closeDialog}>
            Cancel
          </Button>
          <Button color="primary" variant="contained" onClick={assignTerritory}>
            Save Details
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={notification.open}
        autoHideDuration={3200}
        onClose={() => setNotification({ open: false, message: '' })}
        message={notification.message}
      />
    </>
  )
}

export default SubcontractorTerritoryMapEditor
